import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Permission modules matching the backend RBAC structure.
 */
export const PERMISSION_MODULES = [
  'contact_management',
  'client_management',
  'job_management',
  'billing',
  'reporting',
  'documents',
  'note_categories',
  'settings',
  'user_admin',
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number];

/**
 * Permission actions available per module.
 */
export const PERMISSION_ACTIONS = ['read', 'create', 'update', 'delete'] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/**
 * Module-specific extra permissions beyond the basic CRUD.
 */
export const MODULE_EXTRA_PERMISSIONS: Partial<
  Record<PermissionModule, { key: string; label: string }[]>
> = {
  billing: [
    { key: 'billing.create_invoice', label: 'Create Invoice' },
    { key: 'billing.update_invoice', label: 'Update Invoice' },
    { key: 'billing.send_invoice', label: 'Send Invoice' },
    { key: 'billing.void_invoice', label: 'Void Invoice' },
    { key: 'billing.record_payment', label: 'Record Payment' },
    { key: 'billing.create_credit_note', label: 'Credit Note' },
    { key: 'billing.create_write_off', label: 'Write Off' },
    { key: 'billing.create_discount', label: 'Discount' },
    { key: 'billing.create_debit_note', label: 'Debit Note' },
    { key: 'billing.create_advance', label: 'Advance' },
    { key: 'billing.manage_quotes', label: 'Manage Quotes' },
    { key: 'billing.manage_recurring', label: 'Recurring Billing' },
    { key: 'billing.view_ledger', label: 'View Client Ledger' },
    { key: 'billing.close_period', label: 'Close Client Period' },
    { key: 'billing.export_ledger', label: 'Export Ledger PDF' },
    { key: 'billing.manage_periods', label: 'Manage Financial Periods' },
  ],
  job_management: [
    { key: 'job_management.create', label: 'Create' },
    { key: 'job_management.transition', label: 'Transition Stages' },
    { key: 'job_management.change_workflow', label: 'Change Workflow' },
    { key: 'job_management.change_status', label: 'Change Status' },
    { key: 'job_management.assign_task_to_client', label: 'Assign to Client' },
    { key: 'job_management.manage_recurrence', label: 'Manage Recurrence' },
  ],
  reporting: [
    { key: 'reporting.generate_reports', label: 'Generate Reports' },
    { key: 'reporting.export_reports', label: 'Export Reports' },
    { key: 'reporting.schedule_reports', label: 'Schedule Reports' },
    { key: 'reporting.view_financials', label: 'View Financials' },
  ],
};

/**
 * Human-friendly labels for modules.
 */
export const MODULE_LABELS: Record<PermissionModule, string> = {
  contact_management: 'Contacts',
  client_management: 'Clients',
  job_management: 'Jobs',
  billing: 'Billing',
  reporting: 'Reporting',
  documents: 'Documents',
  note_categories: 'Notes / Reminders',
  settings: 'Settings',
  user_admin: 'User Admin',
};

/**
 * Data scope options for a role.
 */
export type DataScope = 'assigned' | 'team' | 'all';

/**
 * Role entity matching the backend data model.
 */
export interface Role {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isSystemRole: boolean;
  dataScope: DataScope;
  permissions: Record<string, boolean>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Payload for creating or updating a role.
 */
export interface RolePayload {
  name: string;
  description?: string;
  dataScope: DataScope;
  permissions: Record<string, boolean>;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const rolesKeys = {
  all: ['roles'] as const,
  lists: () => [...rolesKeys.all, 'list'] as const,
  detail: (id: string) => [...rolesKeys.all, 'detail', id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all roles for the tenant.
 */
export function useRoles() {
  return useQuery({
    queryKey: rolesKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Role[]>>('/roles');
      return response.data.data;
    },
  });
}

/**
 * Fetch a single role by ID.
 */
export function useRole(id: string) {
  return useQuery({
    queryKey: rolesKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Role>>(`/roles/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

/**
 * Create a new role.
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: RolePayload) => {
      const response = await apiClient.post<ApiResponse<Role>>('/roles', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}

/**
 * Update an existing role.
 */
export function useUpdateRole(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<RolePayload>) => {
      const response = await apiClient.put<ApiResponse<Role>>(`/roles/${id}`, payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: rolesKeys.detail(id) });
    },
  });
}

/**
 * Delete (deactivate) a role.
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/roles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}

/**
 * Clone a role — creates a new role with the same permissions.
 */
export function useCloneRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceRole: Role) => {
      const payload: RolePayload = {
        name: `${sourceRole.name} (Copy)`,
        description: sourceRole.description,
        dataScope: sourceRole.dataScope,
        permissions: { ...sourceRole.permissions },
      };
      const response = await apiClient.post<ApiResponse<Role>>('/roles', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}
